import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Alert,
  Button,
  Spinner,
  Table,
  TableHead,
  TableHeadCell,
  TableRow,
  TableCell,
  TableBody,
  TextInput,
  Select,
} from "flowbite-react";

import RegionFormModal from "../components/regions/RegionFormModal";
import { regionsActions, regionsThunks, statesThunks } from "../app/store"; // ajuste conforme seu alias/import
// (no seu CountiesPage você importa de "../app/store") :contentReference[oaicite:2]{index=2}

export default function RegionsPage() {
  const dispatch = useDispatch();

  const { items, count, status, error } = useSelector((s) => s.regions);
  const statesById = useSelector((s) => s.states.byId);

  const [q, setQ] = useState("");
  const [stateId, setStateId] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const limit = useMemo(() => 20, []);
  const offset = 0;

  useEffect(() => {
    dispatch(statesThunks.list({ q: "", limit: 9999, offset: 0 }));
    dispatch(regionsThunks.list({ q: "", limit, offset }));
  }, [dispatch, limit]);

  function openCreate() {
    setEditing(null);
    setModalOpen(true);
  }

  function openEdit(row) {
    setEditing(row);
    setModalOpen(true);
  }

  function closeModal() {
    setModalOpen(false);
    setEditing(null);
    dispatch(regionsActions.clearError());
  }

  async function onSubmit(payload) {
    if (editing?.id) {
      await dispatch(regionsThunks.updateOne({ id: editing.id, payload }));
      dispatch(
        regionsThunks.list({
          q: q || "",
          state_id: stateId || undefined,
          limit,
          offset,
        }),
      );
      closeModal();
      return;
    }

    await dispatch(regionsThunks.createOne(payload));
    dispatch(
      regionsThunks.list({
        q: q || "",
        state_id: stateId || undefined,
        limit,
        offset,
      }),
    );
    closeModal();
  }

  function onSearch() {
    dispatch(regionsActions.clearError());
    dispatch(
      regionsThunks.list({
        q: q || "",
        state_id: stateId || undefined,
        limit,
        offset,
      }),
    );
  }

  const isLoading = status === "loading";

  const stateOptions = useMemo(() => {
    const arr = Object.values(statesById || {});
    return arr.sort((a, b) =>
      String(a?.name || "").localeCompare(String(b?.name || "")),
    );
  }, [statesById]);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">
            Regions
          </h1>
          <p className="text-sm text-gray-500">{count} registro(s)</p>
        </div>

        <Button className="text-slate-600" onClick={openCreate}>
          Nova Region
        </Button>
      </div>

      {error && (
        <Alert
          color="failure"
          onDismiss={() => dispatch(regionsActions.clearError())}
        >
          {error}
        </Alert>
      )}

      <div className="flex flex-col md:flex-row md:items-center gap-2">
        <TextInput
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Buscar por name..."
        />

        <Select value={stateId} onChange={(e) => setStateId(e.target.value)}>
          <option value="">Todos os States</option>
          {stateOptions.map((st) => (
            <option key={st.id} value={st.id}>
              {st.code} - {st.name}
            </option>
          ))}
        </Select>

        <Button color="gray" onClick={onSearch} disabled={isLoading}>
          Buscar
        </Button>
      </div>

      <div className="border border-gray-200 dark:border-gray-800 rounded-lg overflow-hidden">
        {isLoading ? (
          <div className="p-6 flex items-center gap-3">
            <Spinner />
            <span className="text-sm text-gray-600 dark:text-gray-300">
              Carregando...
            </span>
          </div>
        ) : (
          <Table hoverable>
            <TableHead>
              <TableHeadCell>State</TableHeadCell>
              <TableHeadCell>Name</TableHeadCell>
              <TableHeadCell className="text-right">Ações</TableHeadCell>
            </TableHead>

            <TableBody className="divide-y">
              {items?.length ? (
                items.map((r) => (
                  <TableRow
                    key={r.id}
                    className="bg-white dark:border-gray-700 dark:bg-gray-800"
                  >
                    <TableCell className="font-medium text-gray-900 dark:text-white">
                      {statesById?.[r.state_id]?.code || "—"}
                    </TableCell>

                    <TableCell>{r.name}</TableCell>

                    <TableCell className="text-right">
                      <Button
                        size="xs"
                        color="gray"
                        onClick={() => openEdit(r)}
                      >
                        Editar
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell
                    colSpan={3}
                    className="text-center py-6 text-gray-500"
                  >
                    Nenhuma region encontrada.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        )}
      </div>

      <RegionFormModal
        open={modalOpen}
        onClose={closeModal}
        initialData={editing}
        onSubmit={onSubmit}
        states={stateOptions}
        saving={isLoading}
      />
    </div>
  );
}
